import './AccessLoadingSpinner.scss';

import { COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { LoadingSpinner } from '@ui/LoadingSpinner';

import React, { useLayoutEffect } from 'react';
import { Skeleton, Table } from 'antd';

type AccessLoadingSpinnerProps = {
	loading?: boolean;
	sorting?: boolean;
	children?: React.ReactNode;
};

export const AccessLoadingSpinner: React.FC<AccessLoadingSpinnerProps> = ({ loading, sorting, children }) => {
	const isSpinning = loading || sorting;

	// Dynamically calculate table header height and set as CSS custom property
	// This ensures the loading overlay starts below the header regardless of header size changes
	useLayoutEffect(() => {
		if (children && isSpinning) {
			const header = document.querySelector('.ant-table-thead');
			if (header) {
				const headerHeight = header.getBoundingClientRect().height;
				document.documentElement.style.setProperty('--table-header-height', `${headerHeight}px`);
			}
		}

		return () => {
			document.documentElement.style.removeProperty('--table-header-height');
		};
	}, [ children, isSpinning ]);

	const loadingTip = loading ?
		COPY_SPECIFIC.GETTING_DATA_FROM_SALESFORCE :
		COPY_SPECIFIC.CRUNCHING_THE_NUMBERS;

	const skeletonTable =
		<Table
			columns={ [ ...Array(4) ].map((_, index) => ({
				render: function render() {
					return (
						<Skeleton
							active
							// eslint-disable-next-line react/no-array-index-key
							key={ index }
							paragraph={ false } />
					);
				}
			})) }
			dataSource={ [ ...Array(5) ].map((_, index) => ({
				key: `key${index}`
			})) }
			pagination={ false }
			rowKey='key' />;

	const content = children ??
		<LoadingSpinner
			className='access-spinner'
			spinning={ isSpinning }
			tip={ loadingTip }>
			{ skeletonTable }
		</LoadingSpinner>;

	return (
		<>
			{ content }
			{ children && isSpinning &&
				<div className='access-spinner transparent-background'>
					<LoadingSpinner
						spinning={ isSpinning }
						tip={ loadingTip }>
						{ /* Empty div required for LoadingSpinner to display the tip text overlay */ }
						<div />
					</LoadingSpinner>
				</div> }
		</>
	);
};
